import type { PageJson } from '@apps/card/routes/CardCustomizer/components/PageJson.types';
import { buildCssVarStyle, imageVarIndices } from '@apps/card/utils/colorway';
import { absolutePhotoUrl, applyRenditionIfMediaServiceUrl } from '@apps/card/utils/mediaService';
import type { Rendition } from '@apps/card/utils/mediaService';
import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Resolves a colorway selection into inline CSS custom properties (`--N`)
 * for live preview, without mutating the draft (the saved draft keeps its
 * `var(--N)` tokens so the backend collapses them at render time).
 */
export function useColorways(page: PageJson | undefined, rendition?: Rendition, colorwayOverride?: string) {
  const colorways = page?.colorways;
  const options = colorways?.options;

  const resolveSelectedId = () => {
    if (colorwayOverride && options?.some(o => o.id === colorwayOverride)) return colorwayOverride;
    if (colorways?.selectedId && options?.some(o => o.id === colorways.selectedId)) return colorways.selectedId;
    return options?.[0]?.id ?? '';
  };

  const [selectedId, setSelectedId] = useState<string>(resolveSelectedId);

  // Components consuming the override don't remount when it changes (e.g. a
  // gallery tile surviving a search-term change), so re-derive the selection
  // during render when the override changes. Adjusting state during render
  // (before paint) rather than in an effect avoids a post-mount flash. A manual
  // swatch selection is preserved while the same override stays active, since
  // this only fires when the override itself changes. The previous value is
  // state, not a ref: a discarded render must not remember it.
  const [prevOverride, setPrevOverride] = useState(colorwayOverride);
  if (colorwayOverride !== prevOverride) {
    setPrevOverride(colorwayOverride);
    setSelectedId(resolveSelectedId());
  }

  // The gallery tile can mount before its theme loads (PRNT-5494), so re-resolve once options
  // arrive if the current selection isn't one of them. A manual pick is still an option, so it stays.
  const [prevOptions, setPrevOptions] = useState(options);
  if (options !== prevOptions) {
    setPrevOptions(options);
    if (!options?.some(o => o.id === selectedId)) setSelectedId(resolveSelectedId());
  }

  const selectedColorway = options?.find(o => o.id === selectedId)?.values;

  const imageIndices = useMemo(() => imageVarIndices(page?.layers ?? []), [page?.layers]);

  const cssVarStyle = useMemo(() => (selectedColorway ? buildCssVarStyle(selectedColorway, imageIndices, rendition) : {}), [selectedColorway, imageIndices, rendition]);

  // Preload all colorway images on first hover so swatch switches are instant.
  const hasPreloaded = useRef(false);
  const preloadImages = useCallback(() => {
    if (hasPreloaded.current || !options) return;
    hasPreloaded.current = true;
    for (const { values } of options) {
      imageIndices.forEach(index => {
        const value = values[index];
        if (value) new Image().src = applyRenditionIfMediaServiceUrl(absolutePhotoUrl(value), rendition);
      });
    }
  }, [options, imageIndices, rendition]);

  return { options, selectedId, setSelectedId, selectedColorway, cssVarStyle, preloadImages };
}
